/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import config from "../config/userLists.config.json";
import axios from "axios";
import { StorageModule } from "@vlinder-web/storage-module-react";
import jwt_decode from "jwt-decode";
import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { GET_PAY_TXS, GET_PAY_TXS_ADMIN, GET_PRESENTATIONS } from "../graphql";
import { VApplication } from "../../../application";
import moment from "moment-timezone";
import { pretty } from "@vlinder-web/utils-react";

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

export interface IUserListsMethods extends IPluginMethodMap {
  getUserListsProps(args: any): Promise<any>;
}

export class UserListsService implements IAgentPlugin {
  readonly methods: IUserListsMethods;

  constructor() {
    this.methods = {
      getUserListsProps: this.getUserListsProps.bind(this),
    };
  }

  async getUserListsProps(args: any) {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    let filter = {};
    let presentationOptions: any = [],
      defaultPresentation: any = "";
    console.log("argsargsargsargsargs", args);
    if (args?.criteria?.criteriaMulti) {
      const searchTypeMulti = this.determineSearchType(
        args.criteria.criteriaMulti
      );
      if (searchTypeMulti) {
        const filter1 =
          searchTypeMulti === "multi"
            ? this.getMultiFilter(args.criteria.criteriaMulti)
            : this.convertCriteria(args.criteria.criteriaMulti);
        filter = { ...filter, ...filter1 };
      }
    }

    if (args?.criteria?.criteria) {
      const searchType = this.determineSearchType(args.criteria.criteria);
      if (searchType) {
        const filter1 =
          searchType === "multi"
            ? this.getMultiFilter(args.criteria.criteria)
            : this.convertCriteria(args.criteria.criteria);
        filter = { ...filter, ...filter1 };
      }
    }

    const options = {
      limit: args?.size ?? 10,
      skip: args?.offset ?? 0,
      sort: {
        field: "updatedAt",
        order: "desc",
      },
    };
    try {
      if (client) {
        const presentation = await client.query({
          query: GET_PRESENTATIONS,
          variables: {
            filter: {},
            options: {
              limit: 10,
              skip: 0,
              sort: {
                field: "status",
                order: "asc",
              },
            },
          },
          fetchPolicy: "network-only",
        });
        if (
          presentation &&
          Array.isArray(presentation?.data?.getPresentations?.content) &&
          presentation?.data?.getPresentations?.content[0]?.id
        ) {
          for (
            let i = 0;
            i < presentation?.data?.getPresentations?.content?.length;
            i++
          ) {
            if (
              presentation?.data?.getPresentations?.content[i]?.status ===
              "active"
            ) {
              defaultPresentation =
                presentation?.data?.getPresentations?.content[i]?.id;
              break;
            }
          }
          presentationOptions =
            presentation?.data?.getPresentations?.content?.map((_item: any) => {
              return { value: _item?.id, label: _item?.name };
            });

          // presentation && Array.isArray(presentation?.data?.getPresentations?.content) && presentation?.data?.getPresentations?.content?.length>0 && presentation?.data?.getPresentations?.content?.forEach((item?:any)=>{
          //   if(item?.status === 'active')
          //   defaultPresentation = item?.id;
          // })

          if (filter?.presentation) {
            filter.presentation = {
              eq: filter?.presentation ?? defaultPresentation,
            };
          } else {
            filter.presentation = {
              eq: defaultPresentation,
            };
          }
        }
        console.log(
          "presentationOptions",
          presentationOptions,
          "defaultPresentation",
          defaultPresentation
        );
        const storageSrv = StorageModule.getInstance();
        let _sessionToken = storageSrv.get(
          "userProfile",
          "session"
        )?.accessToken;
        let _localToken = storageSrv.get("userProfile", "local")?.accessToken;
        let _token = _localToken ?? _sessionToken;
        const tokenRole = jwt_decode(_token);
        const res = await client.query({
          query:
            tokenRole?.currentRole?.name === "production"
              ? GET_PAY_TXS
              : GET_PAY_TXS_ADMIN,
          variables: {
            options: options,
            filters: filter,
          },
          fetchPolicy: "network-only",
        });
        let defFilter = [
          {
            key: "Presentation",
            filters: {
              Equal: defaultPresentation,
              label: presentationOptions && presentationOptions[0]?.label,
              inputData: {
                id: "presentation",
                options: presentationOptions,
              },
            },
          },
        ];

        if (res?.data) {
          console.log(
            "res?.data?.getPayTxs?.content",
            pretty(res?.data?.getPayTxs?.content)
          );
          let _rowData = this.formatUserTable(res?.data?.getPayTxs?.content);

          let _totalPages = res?.data?.getPayTxs?.count
            ? Math.ceil(parseInt(res?.data?.getPayTxs?.count) / 10)
            : 1;

          let _currentPage =
            res?.data?.getPayTxs?.skip / res?.data?.getPayTxs?.limit + 1;
          return {
            ...config,
            ultUsers: _rowData,
            isEmpty: res?.data?.getPayTxs?.content?.length <= 0,
            activePage: _currentPage,
            currentPage: _currentPage,
            pageSize: res?.data?.getPayTxs?.limit,
            totalCount: res?.data?.getPayTxs?.count,
            totalPages: _totalPages,
            defaultFilter: defFilter,
          };
        }
      }
    } catch (err: any) {
      let _display_message =
        err?.graphQLErrors &&
        Array.isArray(err?.graphQLErrors) &&
        err?.graphQLErrors.find((item: any) => item.extensions);
      if (_display_message?.extensions?.display_message) {
        throw Error(_display_message?.extensions?.display_message);
      }

      throw err;
    }
  }

  private formatAmount(amount: any): string {
    const num = Number(amount);
    if (isNaN(num)) return "0.00";
    return num.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  private isOfflinePayment(item: any): boolean {
    const payMethod = String(item?.payMethod || "").trim().toLowerCase();
    const mode = String(item?.mode || "").trim().toLowerCase();
    const offlineValues = ["offline", "cash", "pos", "cheque", "check"];
    return offlineValues.includes(payMethod) || offlineValues.includes(mode);
  }

  private getPaymentDisplay(item: any): { amount: string; currency: string } {
    const isOffline = this.isOfflinePayment(item);

    if (isOffline) {
      const rawAmount =
        item?.actualPayment?.amount !== null &&
        item?.actualPayment?.amount !== undefined
          ? item?.actualPayment?.amount
          : item?.amount;

      const amount =
        rawAmount !== null &&
        rawAmount !== undefined &&
        !isNaN(Number(rawAmount))
          ? this.formatAmount(rawAmount)
          : rawAmount !== null && rawAmount !== undefined && rawAmount !== "Null"
          ? String(rawAmount)
          : "-";

      return {
        amount,
        currency: "TTD",
      };
    }

    // Online Payments
    const rawAmount =
      item?.actualPayment?.amount !== null &&
      item?.actualPayment?.amount !== undefined
        ? item?.actualPayment?.amount
        : item?.amount;

    const amount =
      rawAmount !== null &&
      rawAmount !== undefined &&
      !isNaN(Number(rawAmount))
        ? this.formatAmount(rawAmount)
        : rawAmount !== null && rawAmount !== undefined && rawAmount !== "Null"
        ? String(rawAmount)
        : "-";

    const rawCurrency =
      item?.actualPayment?.currency || item?.currency || "TTD";

    const currency =
      typeof rawCurrency === "string" ? rawCurrency.toUpperCase() : rawCurrency;

    return {
      amount,
      currency,
    };
  }

  private formatUserTable(data: any) {
    let _rowData: any = [];
    data.forEach((item: any) => {
      const { amount, currency } = this.getPaymentDisplay(item);

      _rowData.push({
        id: item?.id,
        amount,
        name: item?.masqUser?.fullName ?? "Null Null",
        userId: item?.masqUser?.id,
        email: item?.masqUser?.email ?? "Null",
        masqId: item?.masqUser?.vuid ?? "Null",
        costumeID: item?.costumeID ?? "-",
        createdAt: item?.createdAt
          ? this.formatCreatedDate(item?.createdAt)
          : "Null",
        txId: item?.txId ?? "-",
        mode: item?.mode ?? "-",
        notes: item?.notes?.[0] ?? "-",
        payMethod: item?.payMethod ?? "_",
        createdByUser: item?.createdByUser?.fullName ?? "-",
        orderId: item?.payOrder?.orderId ?? "-",
        paymentStatus: item?.payOrder?.paymentStatus ?? "-",
        currency,
        reciptId: item?.reciptId ?? "-",
        status: item?.status ?? "-",
        resolutionStatus: item?.resolutionStatus ?? "-",
        updatedAt: item?.updatedAt
          ? this.formatCreatedDate(item?.updatedAt)
          : "-",
        allDetails: item,
      });
    });
    return _rowData;
  }

  // private determineSearchType(criteria: any) {
  //   if (Object.keys(criteria).length === 0) {
  //     return null;
  //   } else if (Object.keys(criteria)[0] === "0") {
  //     return "multi";
  //   } else {
  //     return "normal";
  //   }
  // }
  private determineSearchType(criteria: any) {
    if (!criteria || typeof criteria !== "object") return null;

    const keys = Object.keys(criteria);
    const isMulti = keys.some((key) => !isNaN(Number(key)));
    const isNormal = keys.some((key) => isNaN(Number(key)));

    // if (isMulti && isNormal) return "both";
    if (isMulti) return "multi";
    if (isNormal) return "normal";
    return null;
  }

  private convertCriteria(criteria: any) {
    const result: any = {};
    for (const key in criteria) {
      let value = criteria[key];
      if (typeof value === "string") {
        // Remove trailing spaces from string values
        value = value.trim();
      }

      if (key === "MasqUser___fullName" || key === "MasqUser___email") {
        result[key] = { contains: value };
      } else {
        result[key] = { eq: value };
      }
    }
    return result;
  }

  private formatCreatedDate(mydate: Date) {
    if (mydate) {
      // return moment(date).format("LL");
      const date = new Date(mydate);

      // Options for formatting the date
      const options: any = {
        month: "short",
        day: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      };

      // Convert to the desired format
      const formattedDate = date
        .toLocaleString("en-US", options)
        .replace(",", "");
      console.log(formattedDate);
      return formattedDate;
    }
  }

  private getMultiFilter(criteria: any) {
    const transformedCriteria: any = {};
    for (const key in criteria) {
      if (criteria.hasOwnProperty(key)) {
        const { key: filterKey, filters } = criteria[key];
        const { inputData } = filters;
        const filterType = Object.keys(filters).find(
          (key) => key !== "inputData"
        );

        if (inputData.id === "presentation") {
          // If the id is "presentation", handle it separately
          transformedCriteria.presentation = filters[filterType];
        }

        // if (inputData.id === "mode") {
        //   // If the id is "Boxes___boxStatus", assign the value directly
        //   transformedCriteria[inputData.id] = filters[filterType];
        // }
        else {
          transformedCriteria[inputData.id] = {
            [filterType.toLowerCase().replace("equal", "eq")]:
              filters[filterType],
          };
        }
      }
    }
    return transformedCriteria;
  }
}
